import { useEffect, useState } from "react";
import type { ProjectImage } from "@/mocks/gallery";

interface ImageGalleryProps {
  images: ProjectImage[];
  title: string;
}

/**
 * Editorial image gallery with lightbox.
 * Layout uses varied aspects to feel like a real magazine spread.
 * Clicking any image opens a full-screen lightbox with keyboard navigation.
 */
export default function ImageGallery({ images, title }: ImageGalleryProps) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenIdx(null);
      if (e.key === "ArrowRight") {
        setOpenIdx((v) => (v === null ? null : (v + 1) % images.length));
      }
      if (e.key === "ArrowLeft") {
        setOpenIdx((v) =>
          v === null ? null : (v - 1 + images.length) % images.length
        );
      }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [openIdx, images.length]);

  const spanFor = (img: ProjectImage) => {
    if (img.aspect === "landscape") return "lg:col-span-8";
    if (img.aspect === "portrait") return "lg:col-span-4";
    return "lg:col-span-6";
  };

  return (
    <section className="relative py-24 md:py-40 px-6 md:px-14 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16 md:mb-20">
          <h2
            className="font-black uppercase leading-[0.95] tracking-[-0.02em] text-white"
            style={{ fontSize: "clamp(32px, 4.4vw, 68px)" }}
          >
            Fragments
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {images.map((img, i) => (
            <button
              key={`${title}-img-${i}`}
              type="button"
              onClick={() => setOpenIdx(i)}
              className={`relative overflow-hidden rounded-lg bg-black cursor-pointer group text-left ${spanFor(img)}`}
              style={{
                aspectRatio:
                  img.aspect === "portrait"
                    ? "3 / 4"
                    : img.aspect === "landscape"
                    ? "16 / 10"
                    : "1 / 1",
              }}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="absolute inset-0 w-full h-full object-cover object-center grayscale brightness-[0.55] transition-[transform,filter] duration-[1200ms] ease-out group-hover:scale-[1.05] group-hover:grayscale-0 group-hover:brightness-100"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent transition-opacity duration-[1200ms] ease-out group-hover:opacity-40" />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                <div className="text-[10px] uppercase tracking-[0.4em] text-white/70">
                  {img.caption}
                </div>
                <span className="w-8 h-8 rounded-full border border-white/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <i className="ri-fullscreen-line text-white text-sm" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {openIdx !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center cursor-pointer"
          onClick={() => setOpenIdx(null)}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenIdx(null);
            }}
            className="absolute top-6 right-6 w-11 h-11 rounded-full border border-white/40 flex items-center justify-center text-white cursor-pointer hover:bg-white/10 transition-colors z-10"
            aria-label="Close lightbox"
          >
            <i className="ri-close-line text-xl" />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenIdx((v) =>
                v === null ? null : (v - 1 + images.length) % images.length
              );
            }}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/40 flex items-center justify-center text-white cursor-pointer hover:bg-white/10 transition-colors z-10"
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenIdx((v) =>
                v === null ? null : (v + 1) % images.length
              );
            }}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/40 flex items-center justify-center text-white cursor-pointer hover:bg-white/10 transition-colors z-10"
            aria-label="Next image"
          >
            <i className="ri-arrow-right-line text-xl" />
          </button>

          <div
            className="relative max-w-[90vw] max-h-[85vh] flex flex-col items-center cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[openIdx].src}
              alt={images[openIdx].alt}
              className="max-w-[90vw] max-h-[80vh] object-contain rounded"
            />
            <div className="mt-4 text-[10px] uppercase tracking-[0.4em] text-white/70 text-center">
              {images[openIdx].caption} · {openIdx + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}